import { API_URL } from '../config/constants';

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  maxRetries?: number;
  retryDelay?: number;
}

export class ImageUploadService {
  static async uploadImage(
    file: File | File[],
    type: 'product' | 'portfolio' = 'product',
    options: UploadOptions = {}
  ): Promise<string | string[]> {
    const {
      onProgress,
      maxRetries = 3,
      retryDelay = 1000
    } = options;

    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        const formData = new FormData();
        const files = Array.isArray(file) ? file : [file];
        
        files.forEach(f => formData.append('files', f));

        const xhr = new XMLHttpRequest();
        
        // Setup progress tracking
        if (onProgress) {
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              onProgress({
                loaded: event.loaded,
                total: event.total,
                percentage: Math.round((event.loaded * 100) / event.total)
              });
            }
          };
        }

        // Create a promise to handle the XHR request
        const response = await new Promise<any>((resolve, reject) => {
          xhr.open('POST', `${API_URL}/api/design/upload?type=${type}`);
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                resolve(response);
              } catch (error) {
                reject(new Error('Invalid response format'));
              }
            } else {
              try {
                const errorData = JSON.parse(xhr.responseText);
                reject(new Error(errorData.error || 'Upload failed'));
              } catch (error) {
                reject(new Error(`Upload failed with status ${xhr.status}`));
              }
            }
          };

          xhr.onerror = () => {
            reject(new Error('Network error occurred'));
          };

          xhr.send(formData);
        });

        return response.urls;
      } catch (error: unknown) {
        attempt++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        console.error(`Upload attempt ${attempt} failed:`, errorMessage);

        if (attempt === maxRetries) {
          throw new Error(`Failed to upload after ${maxRetries} attempts: ${errorMessage}`);
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }

    throw new Error('Upload failed');
  }

  static getImageUrl(path: string): string {
    if (!path) return '/placeholder-image.png';
    
    // If it's already a full URL, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // If it's a relative path starting with /uploads, prepend the API URL
    if (path.startsWith('/uploads')) {
      return `${API_URL}${path}`;
    }

    // Default case: treat as relative path
    return `${API_URL}/uploads/${path}`;
  }

  static validateImage(file: File): string | null {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Please upload JPEG, PNG, or WebP images.';
    }

    if (file.size > maxSize) {
      return 'File is too large. Maximum size is 5MB.';
    }

    return null;
  }

  static async validateImages(files: File[]): Promise<{ valid: File[]; errors: { file: File; error: string }[] }> {
    const results = await Promise.all(
      files.map(async file => ({
        file,
        error: this.validateImage(file)
      }))
    );

    return {
      valid: results.filter(r => !r.error).map(r => r.file),
      errors: results.filter(r => r.error).map(r => ({ file: r.file, error: r.error! }))
    };
  }
} 